import { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { BULK_APPROVE_CPM_RANKING_CLIPS, BULK_REJECT_CPM_RANKING_CLIPS } from '@/queries/cpmRanking';
import type {
  AdminBulkApproveCpmRankingClipsMutation, AdminBulkApproveCpmRankingClipsMutationVariables,
  AdminBulkRejectCpmRankingClipsMutation, AdminBulkRejectCpmRankingClipsMutationVariables,
} from '@/generated/cpm-ranking';
import type { RankingBulkAction } from '@/lib/campaigns/rankingBulkReview';

export type RankingBulkFeedback = { kind: 'done'; action: RankingBulkAction; reviewed: number; skipped: number } | { kind: 'failed' };
const sessionToken = () => { try { return localStorage.getItem('token'); } catch { return null; } };

/** One bulk request at a time; a stale session or campaign never refreshes or reports into a new one. */
export function useRankingBulkReview({ campaignId, phase = '' }: { campaignId: string; phase?: string }) {
  const client = useApolloClient();
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState<RankingBulkFeedback | null>(null);
  const token = sessionToken();
  const scope = JSON.stringify([token, campaignId, phase]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const mounted = useRef(true);
  const request = useRef(0);
  const locked = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; request.current += 1; };
  }, []);
  useEffect(() => {
    request.current += 1; locked.current = false;
    setPending(false); setFeedback(null);
  }, [scope]);
  const decide = async (action: RankingBulkAction, ids: string[], reason: string) => {
    if (action === 'approve') {
      const result = await client.mutate<AdminBulkApproveCpmRankingClipsMutation, AdminBulkApproveCpmRankingClipsMutationVariables>({ mutation: BULK_APPROVE_CPM_RANKING_CLIPS, fetchPolicy: 'no-cache', errorPolicy: 'all', variables: { campaignId, ids } });
      const payload = result.data?.adminBulkApproveCpmRankingClips;
      return { errors: result.errors, payload: payload && { reviewed: payload.approved, skipped: payload.skipped } };
    }
    const result = await client.mutate<AdminBulkRejectCpmRankingClipsMutation, AdminBulkRejectCpmRankingClipsMutationVariables>({ mutation: BULK_REJECT_CPM_RANKING_CLIPS, fetchPolicy: 'no-cache', errorPolicy: 'all', variables: { campaignId, ids, ...(reason.trim() ? { reason: reason.trim() } : {}) } });
    const payload = result.data?.adminBulkRejectCpmRankingClips;
    return { errors: result.errors, payload: payload && { reviewed: payload.rejected, skipped: payload.skipped } };
  };
  /** Resolves with the decided submission ids, or null when nothing should change locally. */
  const review = async (action: RankingBulkAction, submissionIds: string[], refresh: () => Promise<unknown>, reason = ''): Promise<string[] | null> => {
    if (!mounted.current || currentScope.current !== scope || sessionToken() !== token) return null;
    if (locked.current || submissionIds.length === 0) return null;
    const ticket = ++request.current;
    const isCurrent = () => mounted.current && request.current === ticket && currentScope.current === scope && sessionToken() === token;
    locked.current = true; setPending(true); setFeedback(null);
    try {
      const { errors, payload } = await decide(action, submissionIds, reason);
      if (!isCurrent()) return null;
      if (errors?.length || !payload) throw new Error('bulk_review_failed');
      // The decisions are saved; a failed reload must not report them as failed.
      await refresh().catch(() => undefined);
      if (!isCurrent()) return null;
      setFeedback({ kind: 'done', action, reviewed: payload.reviewed.length, skipped: payload.skipped.length });
      return payload.reviewed;
    } catch {
      if (isCurrent()) setFeedback({ kind: 'failed' });
      return null;
    } finally {
      if (isCurrent()) { locked.current = false; setPending(false); }
    }
  };
  return { pending, feedback, review };
}
